import { useMapEvents } from 'react-leaflet';
import * as turf from '@turf/turf';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {MapArrowIcon, WarningErrorChip} from '../../../../../components/buttons/IconButtons';
import CustomCard from '../../../../../components/CustomCard';
import {useEffect, useRef, useState} from "react";
import {MODEL_MAP_TYPE} from "../../Model/Model";
import {calculateHeight} from "../../../../../Apis/functions";

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle: Full Stack Developer - IBL
 * @Email: clopez@ibl.mx
 *
 * @param {function} onMarkerPlaced: function to as market
 * @param {Array} geofences - Array of geogeofence objects to display as polygons or rectangles.
 * @param classNameMap
 * @param marginBottom
 */
const GeoFencesMapApp = ({
                             onMarkerPlaced,
                             geofences,
                             classNameMap,
                             marginBottom
}) => {
    const [mapType, setMapType] = useState(MODEL_MAP_TYPE.MAP);
    const elementRefMap = useRef(null);
    const [height, setHeight] = useState(null);
    const { t } = useTranslation();
    const tileUrls = {
        map: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    };

    /**
     * Adjusts the height of the map container dynamically so that it fits
     * from its current top position to the bottom of the viewport.
     *
     * If `classNameMap` is provided, the height adjustment is skipped.
     *
     * The height is recalculated on window resize to remain responsive.
     *
     * @effect
     * - Sets the container height to `window.innerHeight - (containerTop + 120)`.
     * - Updates the `height` state with the calculated value.
     * - Adds a "resize" event listener to recalculate height dynamically.
     * - Cleans up the "resize" listener on component unmount.
     */
    useEffect(() => {
        if (classNameMap !== null) return;
        calculateHeight({elementRef: elementRefMap, setHeight, bottom: marginBottom})
    }, []);

    const MapClickHandler = () => {
        useMapEvents({
            click(e) {
                const { lat, lng } = e.latlng;
                const isInside = geofences.some((geo) => {
                    if (!geo?.coordinates?.length) return false;
                    const polygon = turf.polygon([geo.coordinates.map(c => [c[1], c[0]])]);
                    const point = turf.point([lng, lat]);
                    return turf.booleanPointInPolygon(point, polygon);
                });
                if (isInside) {
                    if(onMarkerPlaced) onMarkerPlaced?.([lat, lng]);
                } else {
                    toast.warning(t('the_marker_must_be_placed_within_the_defined_area'));
                }
            },
        });
        return null;
    };

    const DisabledMap = ({ disabled, className = '' }) => {
        return <>
            <div
                className={`map-overlay ${className} show-overlay-map ${disabled ? "disabled" : ""}`}
            >
                <div className="d-flex flex-column align-items-center justify-content-center">
                    <span className={`${disabled ? "" : "map-arrow"} mb-3`}>
                        <MapArrowIcon className="icon-color-shades-400"/>
                    </span>
                    <p className="fs-14px ibl-font-w-600 font-color-shades-400 mb-0">
                        {t("click_location_in_the_map")}
                    </p>
                </div>
            </div>
        </>
    }

    const CardEmptyFields = () => {
        return (
            <div className={"position-absolute z-index-10000 bottom-0"}>
                <CustomCard
                    classCard={`card-border-8px border-radius-bottom card-error-code-150-100`}
                    classContent="p-10px"
                >
                    <div className="d-flex justify-content-center align-items-center gap-4">
                        <div><WarningErrorChip width={36} height={36} className={"icon-color-error-code-700"}/></div>
                        <div className="d-flex justify-content-center flex-column align-items-start">
                            <p className="fs-14px ibl-font-w-600 font-color-error-700">{t("pin_the_location")}</p>
                            <p className="fs-14px ibl-font-w-400 font-color-error-700">{t("please_click_on_the_map_to_pin_the_fine_location_before_continuing")}</p>
                        </div>
                    </div>
                </CustomCard>
            </div>
        );
    }

    return {
        tileUrls,
        mapType,
        setMapType,
        elementRefMap,
        height,
        setHeight,
        MapClickHandler,
        DisabledMap,
        CardEmptyFields
    };
}
export default GeoFencesMapApp
